/**
 * Route definitions for workspace end-user pages only.
 *
 * Per HLD-005 §2.4, the workspace app contains only:
 *   R-FE-25: Agent grid
 *   R-FE-26: Chat UI
 *   R-FE-27: Task runner (document processor — via plugin extension)
 *   R-FE-28: Report history (via plugin extension)
 *   R-FE-29: Auth flow
 *
 * Document processor and report history UIs are now served through the
 * plugin extension system via PluginExtensionLoader. Only core routes
 * (agent grid, agent interaction, auth) remain as hardcoded routes.
 *
 * @module apps/workspace/routes
 * @see TASK-DPP-DYNAMIC-AGENT-ROUTING-CHG-002
 */
import React, { Suspense, lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { RouteErrorBoundary } from "../../components/shared/RouteErrorBoundary";
import { RequireAuth } from "./AuthWrapper";
import { NotFoundPage } from "../../feature-gate";

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// ---------------------------------------------------------------------------

const AgentsPage = lazy(
  () => import("../../pages/workspace/AgentsPage"),
);

const AgentPage = lazy(
  () => import("../../pages/workspace/AgentPage"),
);

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function PageLoader(): React.ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "50vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Route wrapper with error boundary and suspense
// ---------------------------------------------------------------------------

function RouteWrapper({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <RouteErrorBoundary>
      <RequireAuth>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </RequireAuth>
    </RouteErrorBoundary>
  );
}

// ---------------------------------------------------------------------------
// Route definitions — end-user only (HLD-005 §2.4)
// ---------------------------------------------------------------------------

export const workspaceRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="/agents" replace />,
  },
  // Legacy compatibility: /dashboard → /agents
  {
    path: "dashboard",
    element: <Navigate to="/agents" replace />,
  },
  // R-FE-25: Agent grid
  {
    path: "agents",
    element: (
      <RouteWrapper>
        <AgentsPage />
      </RouteWrapper>
    ),
  },
  // R-FE-26 / R-FE-27 / R-FE-28: Agent interaction + plugin sub-pages
  {
    path: "agents/:agentId/*",
    element: (
      <RouteWrapper>
        <AgentPage />
      </RouteWrapper>
    ),
  },
  // Catch-all: genuinely-unknown URLs render the shared 404 NotFoundPage
  // (SPEC-FEATURE-TOGGLES-RULE-005) instead of redirecting. This is the
  // destination for routes OMITTED by the Feature Gate (a disabled release
  // toggle) as well as truly-unknown paths.
  //
  // This ALSO preserves the absolute-path guard the previous `Navigate
  // to="/agents"` was written to satisfy: /test:prod-loop Stage C.2 discovered
  // that a RELATIVE redirect (`to="agents"`) resolved against the current URL,
  // so hitting (say) /invitations/x/accept would redirect to
  // /invitations/x/accept/agents, which is also unknown, which catches again
  // → /invitations/x/accept/agents/agents, and so on, producing an
  // infinite-depth path explosion and a hard browser freeze. NotFoundPage
  // renders a 404 IN PLACE (no navigation at all), so there is no redirect to
  // resolve and therefore no path-explosion freeze can recur.
  {
    path: "*",
    element: <NotFoundPage homePath="/agents" />,
  },
];

export default workspaceRoutes;
